import { useMemo } from "react";

import { Box, Flex, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useMask } from "@react-input/mask";
import moment from "moment";
import { useTranslation } from "react-i18next";

import {
  Checkbox,
  DatePicker,
  GenderRadioGroup,
  InputLabel,

  Select,
  Text,
  TextInput,
  TimePicker,
} from "components";

import { convertToTimeString } from "components/time";
import { AppConstants } from "constant";
import { formClasses } from "styles";
import { BankCreditOptionsResponse, BankCreditRequestForm } from "types";
import { AppUtils } from "utils";

import SectionTitle from "../../../components/section-title";

const { MASK_SWISS_PHONE_NUMBER } = AppConstants;

interface BankCreditRequestPersonalPartnerProps {
  form: UseFormReturnType<BankCreditRequestForm>;
  options: BankCreditOptionsResponse;
  visibleFields: string[];
  readOnly?: boolean;
}

export const BankCreditRequestPersonalPartner = ({
  form,
  options,
  visibleFields,
  readOnly = false,
}: BankCreditRequestPersonalPartnerProps) => {
  const { t } = useTranslation();

  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });

  const nationalities = useMemo(
    () => AppUtils.getOrderedCountryList(options["nationality"], t),
    [options, t],
  );

  return (
    <Box className={formClasses.sectionBorder}>
      <SectionTitle label={t("credit.partner.label")} isBankCredit />

      {visibleFields.includes("partnerGender") && (
        <Box mb="md">
          <GenderRadioGroup
            label={t("credit.partnerGender.label")}
            fieldName="partnerGender"
            form={form}
            genderOptions={options["gender"]}
            disabled={readOnly}
          />
        </Box>
      )}

      <SimpleGrid cols={4} mt={"sm"}>
        {visibleFields.includes("partnerFirstName") && (
          <TextInput
            label={t("credit.partnerFirstName.label")}
            key={form.key("partnerFirstName")}
            {...form.getInputProps("partnerFirstName")}
            required={true}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("partnerLastName") && (
          <TextInput
            label={t("credit.partnerLastName.label")}
            key={form.key("partnerLastName")}
            {...form.getInputProps("partnerLastName")}
            required={true}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("partnerBirthDate") && (
          <DatePicker
            label={t("credit.partnerBirthDate.label")}
            placeholder={t("user.birthDate.placeholder")}
            key={form.key("partnerBirthDate")}
            {...form.getInputProps("partnerBirthDate")}
            minDate={moment().subtract(65, "years")}
            maxDate={moment().subtract(18, "years")}
            required={true}
            disabled={readOnly}
          />
        )}
      </SimpleGrid>

      <SimpleGrid cols={4} mt={"sm"}>
        {visibleFields.includes("partnerPhoneNumber") && (
          <Box>
            <InputLabel label={t("credit.partner.phone")} />
            <Flex justify={"space-between"}>
              <TextInput
                key={form.key("partnerCountryCode")}
                {...form.getInputProps("partnerCountryCode")}
                w={"30%"}
                disabled={true}
              />
              <TextInput
                key={form.key("partnerPhoneNumber")}
                {...form.getInputProps("partnerPhoneNumber")}
                w={"65%"}
                ref={phoneNumberRef}
                disabled={readOnly}
              />
            </Flex>
          </Box>
        )}
        {visibleFields.includes("partnerEmail") && (
          <TextInput
            label={t("credit.partnerEmail.label")}
            key={form.key("partnerEmail")}
            {...form.getInputProps("partnerEmail")}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("partnerWorkdayAvailabilityFrom") &&
          visibleFields.includes("partnerWorkdayAvailabilityTo") && (
            <Box>
              <InputLabel label={t("credit.workdayAvailability.label")} />
              <Flex align={"center"} justify={"space-evenly"}>
                <Text size={"sm"} c={"var(--mantine-color-gray-6)"}>
                  {t("credit.workdayAvailability.from")}
                </Text>
                <TimePicker
                  key={form.key("partnerWorkdayAvailabilityFrom")}
                  {...form.getInputProps("partnerWorkdayAvailabilityFrom")}
                  min={options["workdayAvailability"].min}
                  max={options["workdayAvailability"].max}
                  w={"30%"}
                  disabled={readOnly}
                />
                <Text size={"sm"} c={"var(--mantine-color-gray-6)"}>
                  {t("credit.workdayAvailability.to")}
                </Text>
                <TimePicker
                  key={form.key("partnerWorkdayAvailabilityTo")}
                  {...form.getInputProps("partnerWorkdayAvailabilityTo")}
                  min={options["workdayAvailability"].min}
                  max={options["workdayAvailability"].max}
                  w={"30%"}
                  disabled={readOnly}
                />
              </Flex>
              <Checkbox
                key={form.key("partnerSaturdayAvailability")}
                label={t("credit.saturdayAvailability.label")}
                {...form.getInputProps("partnerSaturdayAvailability", { type: "checkbox" })}
                customOnClick={() => {
                  form.setFieldValue(
                    "partnerSaturdayAvailabilityFrom",
                    convertToTimeString(options["saturdayAvailability"]?.min || 0),
                  );
                  form.setFieldValue(
                    "partnerSaturdayAvailabilityTo",
                    convertToTimeString(options["saturdayAvailability"]?.max || 0),
                  );
                }}
                disabled={readOnly}
              />
            </Box>
          )}
        {visibleFields.includes("partnerSaturdayAvailabilityFrom") &&
          visibleFields.includes("partnerSaturdayAvailabilityTo") && (
            <Box>
              <InputLabel label={t("credit.saturdayAvailability.label")} />
              <Flex align={"center"} justify={"space-evenly"}>
                <Text size={"sm"} c={"var(--mantine-color-gray-6)"}>
                  {t("credit.saturdayAvailability.from")}
                </Text>
                <TimePicker
                  key={form.key("partnerSaturdayAvailabilityFrom")}
                  {...form.getInputProps("partnerSaturdayAvailabilityFrom")}
                  min={options["saturdayAvailability"].min}
                  max={options["saturdayAvailability"].max}
                  w={"30%"}
                  disabled={readOnly}
                />
                <Text size={"sm"} c={"var(--mantine-color-gray-6)"}>
                  {t("credit.saturdayAvailability.to")}
                </Text>
                <TimePicker
                  key={form.key("partnerSaturdayAvailabilityTo")}
                  {...form.getInputProps("partnerSaturdayAvailabilityTo")}
                  min={options["saturdayAvailability"].min}
                  max={options["saturdayAvailability"].max}
                  w={"30%"}
                  disabled={readOnly}
                />
              </Flex>
            </Box>
          )}
      </SimpleGrid>

      {visibleFields.includes("partnerNationality") && (
        <SimpleGrid cols={4} mt={"sm"}>
          <Select
            label={t("credit.partnerNationality.label")}
            data={nationalities}
            key={form.key("partnerNationality")}
            {...form.getInputProps("partnerNationality")}
            clearable={true}
            required={true}
            disabled={readOnly}
          />
          {visibleFields.includes("partnerVisaType") && (
            <Select
              label={t("credit.partnerVisaType.label")}
              data={options["visaType"].map((el) => ({
                value: el,
                label: t(`user.visa.options.${el}`),
              }))}
              key={form.key("partnerVisaType")}
              {...form.getInputProps("partnerVisaType")}
              clearable={true}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes("partnerVisaFrom") && (
            <DatePicker
              label={t("credit.partnerVisaType.from")}
              key={form.key("partnerVisaFrom")}
              {...form.getInputProps("partnerVisaFrom")}
              maxDate={new Date()}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes("partnerVisaTo") && (
            <DatePicker
              label={t("credit.partnerVisaType.to")}
              key={form.key("partnerVisaTo")}
              {...form.getInputProps("partnerVisaTo")}
              minDate={new Date()}
              required={true}
              disabled={readOnly}
            />
          )}
        </SimpleGrid>
      )}
    </Box>
  );
};
